import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { InputAdornment, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import StarIcon from "@mui/icons-material/Star";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { SoftButton, SoftFooter, SoftRowSkeleton, SoftTag, cls } from "../hatim/soft/SoftKit";
import { useAccessRequestsQuery, useListPackagesQuery } from "../../api/libraryApi";
import {
  SORTS,
  TABS,
  emptyKey,
  normalizePackage,
  openProposalLabel,
  packageMeta,
  pendingIncomingCount,
  readListParams,
  reviewBadge,
  summaryLine,
  usageLine,
  withLatestDecision,
  writeListParams,
} from "./libraryRules";
import {
  CenterMessage,
  LIBRARY_HOME,
  LibraryPanel,
  LockedPackageRow,
  Segment,
  packagePath,
  useAccessRequester,
} from "./LibraryKit";
import LibraryImportDrawer from "./LibraryImportDrawer";

/** Tek sayfa (20 paket); "Daha fazla" ile sonraki sayfa eklenir. */
function PackagePage({ args, page, onHasMore, renderRow }) {
  const { t } = useScriptMode();
  const { data, isFetching, isError, error, refetch } = useListPackagesQuery({ ...args, page });
  const packages = (data?.data?.packages || []).map(normalizePackage);
  const hasMore = Boolean(data?.data?.hasMore);
  useEffect(() => {
    if (data) onHasMore(page, hasMore);
  }, [data, hasMore, page, onHasMore]);
  if (!data && isFetching) return page === 1 ? <SoftRowSkeleton rows={5} /> : null;
  if (isError && !data) {
    return <CenterMessage text={error?.message || t("mzq_packages_failed")} danger onRetry={refetch} error={error} />;
  }
  if (page === 1 && packages.length === 0) return <CenterMessage text={t(emptyKey(args.tab))} />;
  return <ul className="tk-stagger grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">{packages.map(renderRow)}</ul>;
}

/**
 * Müzakere Kütüphanesi (mobil library/index): Paketlerim (özet satırı,
 * izin istekleri, açık öneriler), Keşfet (onaylı paketler, "Yalnız Terakki
 * tavsiyeleri"), Yıldızlılarım; arama, sıralama ve sayfalama. Sekme,
 * sıralama, süzgeç ve arama adreste tutulur. Altta Yeni paket.
 */
export default function LibraryPage() {
  const { t } = useScriptMode();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = readListParams(params);
  const { tab, sort, recommended, q } = state;
  const [search, setSearch] = useState(q);
  const [pageCount, setPageCount] = useState(1);
  const [hasMoreByPage, setHasMoreByPage] = useState({});
  // İzin istenen kilitli satırlar (sunucu yeniden sorulana kadar).
  const [patched, setPatched] = useState({});
  const { requesting, request } = useAccessRequester();
  const [importOpen, setImportOpen] = useState(false);

  const setState = useCallback(
    (patch) => setParams(writeListParams({ ...readListParams(params), ...patch }), { replace: true }),
    [params, setParams]
  );

  // Yazma durunca aramayı adrese yaz.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (search.trim() !== q) setState({ q: search.trim() });
    }, 350);
    return () => clearTimeout(handle);
  }, [search, q, setState]);

  const args = { tab, sort, q, recommended };
  const argsKey = `${tab}|${sort}|${q}|${recommended ? 1 : 0}`;
  useEffect(() => {
    setPageCount(1);
    setHasMoreByPage({});
  }, [argsKey]);

  // Özet (yalnız Paketlerim) ilk sayfanın önbelleğinden okunur.
  const { data: firstPage } = useListPackagesQuery({ ...args, page: 1 });
  const summary = tab === "mine" ? firstPage?.data?.summary : null;
  const { data: incoming = [] } = useAccessRequestsQuery({ role: "incoming" }, { skip: tab !== "mine" });
  const { data: outgoing = [] } = useAccessRequestsQuery({ role: "outgoing" }, { skip: tab === "mine" });
  const pendingRequests = pendingIncomingCount(incoming);

  const reportHasMore = useCallback(
    (page, more) => setHasMoreByPage((prev) => (prev[page] === more ? prev : { ...prev, [page]: more })),
    []
  );

  const renderRow = (raw) => {
    const item = patched[raw.id] ? { ...raw, ...patched[raw.id] } : raw;
    if (item.locked) {
      return (
        <LockedPackageRow
          key={item.id}
          pkg={withLatestDecision(item, outgoing)}
          requesting={requesting === item.id}
          onRequest={() => request(item, (updated) => setPatched((p) => ({ ...p, [item.id]: updated })))}
          to={packagePath(item.id)}
        />
      );
    }
    const proposalsLabel = openProposalLabel(item.openProposalCount, t);
    const badge = reviewBadge(item, { showVerified: false });
    const usage = usageLine(item.usage, t);
    const RowIcon = item.forkedFrom ? CallSplitIcon : MenuBookOutlinedIcon;
    return (
      <li key={item.id} className="min-w-0">
        <Link
          to={packagePath(item.id)}
          className="h-full w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-terakkiBorder bg-terakkiSurface hover:border-terakkiPrimary hover:-translate-y-0.5 hover:shadow-md transition-all dark:bg-white/5 dark:border-white/15 dark:hover:border-white/40"
        >
          <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
            <RowIcon sx={{ fontSize: 18 }} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className={`text-md line-clamp-2 break-words ${cls.title}`}>{item.title}</span>
              {item.verified ? (
                item.recommended ? (
                  <MilitaryTechOutlinedIcon sx={{ fontSize: 16 }} className={cls.accent} titleAccess={t("lib_badge_recommended")} />
                ) : (
                  <VerifiedOutlinedIcon sx={{ fontSize: 15 }} className={cls.accent} titleAccess={t("lib_badge_verified")} />
                )
              ) : null}
              {item.starredByMe ? <StarIcon sx={{ fontSize: 15, color: "#E8A23A" }} titleAccess={t("lib_starred")} /> : null}
            </span>
            <span className={`block text-sm truncate mt-0.5 ${cls.muted}`}>{packageMeta(item, tab, t)}</span>
            {usage ? <span className={`block text-sm truncate ${cls.muted}`}>{usage}</span> : null}
          </span>
          {proposalsLabel ? (
            <SoftTag>{proposalsLabel}</SoftTag>
          ) : badge ? (
            <SoftTag>{t(badge)}</SoftTag>
          ) : item.isMine && item.forkedFrom ? (
            <SoftTag>{t("lib_badge_forked")}</SoftTag>
          ) : null}
          <ChevronRightIcon fontSize="small" className={cls.muted} />
        </Link>
      </li>
    );
  };

  const line = summary ? summaryLine(summary, t) : null;

  return (
    <>
    <LibraryPanel
      wide
      Icon={LocalLibraryOutlinedIcon}
      title={t("lib_title")}
      subtitle={t("lib_subtitle")}
      footer={
        <SoftFooter>
          <div className="w-full flex flex-col sm:flex-row gap-2">
            <SoftButton
              tone="soft"
              fullWidth
              startIcon={<UploadFileOutlinedIcon fontSize="small" />}
              onClick={() => setImportOpen(true)}
            >
              {t("lib_import_cta")}
            </SoftButton>
            <SoftButton
              tone="primary"
              fullWidth
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => navigate(`${LIBRARY_HOME}/yeni`)}
            >
              {t("lib_new_package")}
            </SoftButton>
          </div>
        </SoftFooter>
      }
    >
      {/* Geniş ekranda: solda denetimler, sağda kendi içinde kayan kart ızgarası */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] lg:h-full lg:min-h-0">
      <aside className="flex flex-col lg:border-r border-terakkiBorder dark:border-white/[0.14] lg:overflow-y-auto">
      <div className={`px-4 pt-3 pb-3 flex flex-col gap-3 ${cls.row}`}>
        <Segment
          value={tab}
          options={TABS}
          label={t("lib_title")}
          onChange={(next) => {
            setPatched({});
            setState({ tab: next, recommended: false });
          }}
        />
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("mzq_search_package")}
          inputProps={{ "aria-label": t("mzq_search_package"), maxLength: 60 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className={cls.muted} />
              </InputAdornment>
            ),
          }}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segment value={sort} options={SORTS} label={t("lib_sort")} small onChange={(next) => setState({ sort: next })} />
          {tab === "explore" ? (
            <label className={`flex items-center gap-2 text-base cursor-pointer ${cls.muted}`}>
              <input
                type="checkbox"
                checked={recommended}
                onChange={(e) => setState({ recommended: e.target.checked })}
                className="accent-terakkiPrimaryDark"
              />
              {t("lib_only_recommended")}
            </label>
          ) : null}
        </div>
        {line ? <p className={`text-sm ${cls.muted}`}>{line}</p> : null}
      </div>

      {tab === "mine" ? (
        <Link
          to={`${LIBRARY_HOME}/istekler`}
          className={`flex items-center gap-3 px-4 py-3 ${cls.row} ${cls.rowHover}`}
        >
          <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
            <LockOpenOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block text-md ${cls.title}`}>{t("lib_requests_title")}</span>
            <span className={`block text-sm ${cls.muted}`}>{t("lib_requests_row_hint")}</span>
          </span>
          {pendingRequests > 0 ? <SoftTag>{pendingRequests}</SoftTag> : null}
          <ChevronRightIcon fontSize="small" className={cls.muted} />
        </Link>
      ) : null}

      {/* Excel ile toplu yükleme: kısa tanıtım */}
      <button
        type="button"
        onClick={() => setImportOpen(true)}
        className={`text-left flex items-center gap-3 px-4 py-3 ${cls.row} ${cls.rowHover}`}
      >
        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
          <UploadFileOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-md ${cls.title}`}>{t("lib_import_cta")}</span>
          <span className={`block text-sm ${cls.muted}`}>{t("lib_import_row_hint")}</span>
        </span>
        <ChevronRightIcon fontSize="small" className={cls.muted} />
      </button>
      </aside>

      <div className="p-3 sm:p-4 lg:overflow-y-auto lg:min-h-0">
      {Array.from({ length: pageCount }, (_, i) => (
        <div key={`${argsKey}-${i + 1}`} className={i > 0 ? "mt-3" : ""}>
          <PackagePage args={args} page={i + 1} onHasMore={reportHasMore} renderRow={renderRow} />
        </div>
      ))}
      {hasMoreByPage[pageCount] ? (
        <div className="flex justify-center py-4">
          <SoftButton tone="soft" onClick={() => setPageCount((n) => n + 1)}>
            {t("lib_more")}
          </SoftButton>
        </div>
      ) : null}
      </div>
      </div>
    </LibraryPanel>
    <LibraryImportDrawer open={importOpen} onClose={() => setImportOpen(false)} />
    </>
  );
}
